/**
 * Page-load telemetry client (issue #643).
 *
 * Fires one fire-and-forget POST per slow page load on /farm or
 * /documents. Sub-1 s loads are filtered upstream by ``usePageLoadTelemetry``;
 * this module is the network transport only.
 *
 * Failures are silent by design — per the issue acceptance criteria
 * we accept some loss on the slow-network case where the user navigates
 * away mid-load rather than surfacing telemetry errors to the user.
 */

export interface ViewportSize {
    width: number;
    height: number;
}

export interface PageLoadTelemetryPayload {
    page: 'morgenlage' | 'documents' | 'farm' | 'gutschrift-check' | 'gutschrift-check-feed' | 'liquiditaetsplanung' | 'deckungsbeitrag' | 'ernteprognose';
    /** e.g. ``"farm:lense=organizations:sub=ohne-typ"`` */
    view: string;
    elapsed_ms: number;
    page_index?: number;
    page_size?: number;
    /** True if React Query served the data from cache initially. */
    cache_hit?: boolean;
    /** Backend X-Request-IDs collected during the load (correlation back to ELK BE timings). */
    request_ids?: string[];
    /** Defaults to ``navigator.userAgent`` if omitted. */
    user_agent?: string;
    /** Defaults to ``{ width: window.innerWidth, height: window.innerHeight }`` if omitted. */
    viewport_size?: ViewportSize;
}

// Path includes the explicit ``/v1`` segment so it matches the BFF's
// generic proxy route at ``/api/v1/{path}`` in the deployed
// nginx + BFF stack (the BFF has no ``/api/telemetry/...`` route, so a
// non-versioned URL would 404 before reaching the backend endpoint).
// Mirrors the convention used by the twin services (``/v1/twin/...``).
const TELEMETRY_URL = '/api/v1/telemetry/page-load';

const requestIds = new Set<string>();

/**
 * Push a backend X-Request-ID off a response into the per-page-load set.
 * Called from the axios response interceptor in ``services/api.ts``.
 */
export function captureRequestId(id: string | undefined | null): void {
    if (typeof id === 'string' && id.length > 0) {
        requestIds.add(id);
    }
}

/**
 * Snapshot the captured request IDs and reset the set.
 *
 * The hook calls this at load resolution so the telemetry payload
 * contains the IDs of the requests that contributed to the load.
 * The Set is module-scoped + global rather than per-page because:
 *   1. Only one primary page-load is in flight at a time in the SPA
 *      navigation model.
 *   2. The axios interceptor cannot easily know which "page load"
 *      a given request belongs to without explicit React-Query meta
 *      threading, which would require touching every hook.
 */
export function snapshotAndClearRequestIds(): string[] {
    const out = [...requestIds];
    requestIds.clear();
    return out;
}

/**
 * Fire-and-forget POST to the page-load telemetry endpoint.
 *
 * Uses ``navigator.sendBeacon`` when available so the request survives
 * tab-close / SPA navigation mid-load. Falls back to ``fetch`` with
 * ``keepalive: true`` for the same reason. All errors are swallowed.
 */
export function postPageLoadTelemetry(payload: PageLoadTelemetryPayload): void {
    postTelemetryBeacon(TELEMETRY_URL, {
        ...payload,
        user_agent: payload.user_agent ?? navigator.userAgent,
        viewport_size:
            payload.viewport_size ?? {
                width: window.innerWidth,
                height: window.innerHeight,
            },
    });
}

/**
 * Fire-and-forget POST of one telemetry payload, sendBeacon first.
 *
 * Extracted from ``postPageLoadTelemetry`` (HOF-90 Part 2) so a second event
 * type gets the same transport rather than a second copy of it. Every path is
 * silent by design — telemetry must never surface an error to a farmer
 * mid-drag — which is exactly why the payload shape is pinned by a backend
 * parity test: a 422 here is invisible, so drift reads as "nobody hits that
 * branch" rather than as a bug.
 *
 * Callers must POST to a path under ``/telemetry/``: that prefix is what the
 * BFF's and the backend's CSRF middleware exempt, and sendBeacon cannot set
 * ``X-CSRF-Token``.
 */
export function postTelemetryBeacon(url: string, payload: unknown): void {
    try {
        const body = JSON.stringify(payload);

        if (typeof navigator.sendBeacon === 'function') {
            const blob = new Blob([body], { type: 'application/json' });
            const queued = navigator.sendBeacon(url, blob);
            if (queued) {
                return;
            }
        }

        // Fallback path. ``keepalive: true`` lets the browser flush the
        // request even after the tab closes, the same property we want
        // sendBeacon for.
        void fetch(url, {
            method: 'POST',
            keepalive: true,
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body,
        }).catch(() => {
            /* silent */
        });
    } catch {
        /* silent */
    }
}
